export type GroupPayload = { name: string; members: string[] }

export default function useBusinessGroups(options?: { includeMembers?: boolean; includeEmpty?: boolean }) {
  const { $api } = useNuxtApp()

  const {
    data: groups,
    pending: isLoading,
    refresh,
  } = useLazyApi(() => `/api/groups`, {
    query: options,
    default: (): Group[] => [],
  })

  const isDeleting = ref(false)
  const deleteGroup = async (groupId: string) => {
    isDeleting.value = true
    try {
      await $api(`/api/groups/${groupId}`, {
        method: 'DELETE',
      })
      await refresh()
    } finally {
      isDeleting.value = false
    }
  }

  return {
    groups,
    isLoading,
    refresh,
    deleteGroup,
    isDeleting,
  }
}

export function useGroupForm(groupId?: MaybeRefOrGetter<string | undefined>) {
  const { $api } = useNuxtApp()

  const group = ref<GroupDetail | null>(null)
  const isLoading = ref(false)
  const isSaving = ref(false)

  const fetchGroup = async () => {
    const id = groupId ? toValue(groupId) : undefined
    if (!id) return

    isLoading.value = true
    try {
      group.value = await $api(`/api/groups/${id}`)
    } finally {
      isLoading.value = false
    }
  }

  const saveGroup = async (data: GroupPayload) => {
    isSaving.value = true
    try {
      const id = groupId ? toValue(groupId) : undefined
      if (id) {
        await $api(`/api/groups/${id}`, {
          method: 'PUT',
          body: data,
        })
      } else {
        await $api(`/api/groups`, {
          method: 'POST',
          body: data,
        })
      }
    } finally {
      isSaving.value = false
    }
  }

  return {
    group,
    isLoading,
    isSaving,
    fetchGroup,
    saveGroup,
  }
}
